import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import {
  getProject,
  getSurvey,
  updateSurveyDefinition,
  updateSurveyStatus,
  updateSurveyMode,
  updateSurveyCustomJs,
  updateSurveySlug,
  updateSurveyLabels,
  updateSurveyWelcomePage,
  uploadLogo,
  sendEmails,
  updateSurveyEmailSettings,
  updateSurveyName,
  deleteSurvey,
} from "../api";
import Papa from "papaparse";

import type { Project, SurveyWithDefinition } from "../api";
import { useTitle } from "../hooks/useTitle";

const API_URL = import.meta.env.VITE_API_URL as string;
const SURVEY_URL = import.meta.env.VITE_SURVEY_URL as string | undefined;
const POLL_INTERVAL = 10_000;

export default function SurveyPage() {
  const { projectId, surveyId } = useParams<{ projectId: string; surveyId: string }>();
  const navigate = useNavigate();
  const [project, setProject] = useState<Project | null>(null);
  const [survey, setSurvey] = useState<SurveyWithDefinition | null>(null);

  const pageTitle = survey
    ? `[${survey.mode === "test" ? "TEST" : "LIVE"}] ${survey.name} — ${project?.name ?? ""}`
    : "Chargement...";
  useTitle(pageTitle);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    settings: false,
    definition: false,
    invitations: false,
    welcomePage: false,
    labels: false,
    customJs: false,
  });

  const toggleSection = (id: string) => {
    setExpandedSections((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const setAllSections = (value: boolean) => {
    setExpandedSections({
      settings: value,
      definition: value,
      invitations: value,
      welcomePage: value,
      labels: value,
      customJs: value,
    });
  };

  useEffect(() => {
    if (!projectId || !surveyId) return;
    let cancelled = false;

    async function load() {
      try {
        const [proj, srv] = await Promise.all([
          getProject(projectId!),
          getSurvey(projectId!, surveyId!),
        ]);
        if (!cancelled) {
          setProject(proj);
          setSurvey(srv);
          setLoading(false);
        }
      } catch {
        if (!cancelled) {
          setError("Impossible de charger le sondage.");
          setLoading(false);
        }
      }
    }

    load();

    const interval = setInterval(async () => {
      if (cancelled) return;
      try {
        const srv = await getSurvey(projectId!, surveyId!);
        if (!cancelled) setSurvey(srv);
      } catch {
        // silently ignore poll errors
      }
    }, POLL_INTERVAL);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [projectId, surveyId]);

  async function handleToggleStatus() {
    if (!survey) return;
    const newStatus = survey.status === "active" ? "inactive" : "active";
    try {
      const updated = await updateSurveyStatus(projectId!, surveyId!, newStatus);
      setSurvey((prev) => (prev ? { ...prev, ...updated } : prev));
    } catch {
      // ignore
    }
  }

  async function handleToggleMode() {
    if (!survey) return;
    const newMode = survey.mode === "production" ? "test" : "production";
    try {
      const updated = await updateSurveyMode(projectId!, surveyId!, newMode);
      setSurvey((prev) => (prev ? { ...prev, ...updated } : prev));
    } catch {
      // ignore
    }
  }

  async function handleDelete() {
    if (!projectId || !surveyId) return;
    const confirm = window.confirm(
      "Êtes-vous sûr de vouloir supprimer ce sondage ? Cette action est irréversible et supprimera TOUTES les réponses."
    );
    if (!confirm) return;

    try {
      await deleteSurvey(projectId, surveyId);
      navigate(`/projects/${projectId}`);
    } catch (err) {
      alert("Erreur lors de la suppression du sondage.");
    }
  }

  if (loading) return <p className="status-msg">Chargement…</p>;
  if (error || !survey) return <p className="status-msg error">{error ?? "Sondage introuvable."}</p>;

  const surveyUrl = SURVEY_URL
    ? survey.slug
      ? `${SURVEY_URL}/${survey.slug}`
      : `${SURVEY_URL}/${projectId}/${surveyId}`
    : null;

  return (
    <main className="project-page">
      <nav className="breadcrumb">
        <Link to="/">Tableau de bord</Link>
        <span>›</span>
        <Link to={`/projects/${projectId}`}>{project?.name ?? projectId}</Link>
        <span>›</span>
        <span>{survey.name}</span>
      </nav>

      <h1>{survey.name}</h1>

      <section className="card">
        <div className="survey-meta">
          <div className="meta-item">
            <span className="meta-label">Statut</span>
            <span className={`badge badge-${survey.status}`}>
              {survey.status === "active" ? "Actif" : "Inactif"}
            </span>
          </div>
          <div className="meta-item">
            <span className="meta-label">Mode</span>
            <span className={`badge badge-${survey.mode}`}>{survey.mode}</span>
          </div>
          <div className="meta-item">
            <span className="meta-label">Slug</span>
            <span className="code-text">{survey.slug || "Aucun"}</span>
          </div>
          <div className="meta-item">
            <span className="meta-label">Réponses</span>
            <span className="count">{survey.responseCount}</span>
          </div>
          <div className="meta-item">
            <span className="meta-label">Créé le</span>
            <span>{new Date(survey.createdAt).toLocaleDateString("fr-CA")}</span>
          </div>
        </div>

        <div className="survey-actions">
          {surveyUrl && (
            <a href={surveyUrl} target="_blank" rel="noreferrer">
              <button type="button">Prévisualiser le sondage</button>
            </a>
          )}
          <button type="button" onClick={handleToggleStatus}>
            {survey.status === "active" ? "Fermer le sondage" : "Activer le sondage"}
          </button>
          <button type="button" onClick={handleToggleMode}>
            {survey.mode === "production" ? "Passer en mode test" : "Passer en production"}
          </button>
          <a href={`${API_URL}/projects/${projectId}/surveys/${surveyId}/export/xlsx`} download>
            <button type="button">Exporter xlsx</button>
          </a>
          <a href={`${API_URL}/projects/${projectId}/surveys/${surveyId}/export/pptx`} download>
            <button type="button">Exporter pptx</button>
          </a>
          <button type="button" onClick={handleDelete} style={{ marginLeft: "auto", color: "#d9534f", borderColor: "#d43f3a" }}>
            Supprimer le sondage
          </button>
        </div>
      </section>

      <div className="expand-collapse-actions">
        <button type="button" onClick={() => setAllSections(true)}>Tout déplier</button>
        <button type="button" onClick={() => setAllSections(false)}>Tout replier</button>
      </div>

      <section className="card">
        <div className="collapsible-header" onClick={() => toggleSection("settings")}>
          <h2>Configuration générale (Nom & Slug)</h2>
          <span className={`caret ${expandedSections.settings ? "expanded" : ""}`}>▼</span>
        </div>
        {expandedSections.settings && (
          <div className="collapsible-content">
            <UpdateMetadataForm
              projectId={projectId!}
              surveyId={surveyId!}
              currentName={survey.name}
              onUpdated={(updated) => setSurvey((prev) => (prev ? { ...prev, ...updated } : prev))}
            />
            <hr style={{ margin: "2rem 0", opacity: 0.2 }} />
            <UpdateSlugForm
              projectId={projectId!}
              surveyId={surveyId!}
              currentSlug={survey.slug ?? ""}
              onUpdated={(updated) => setSurvey((prev) => (prev ? { ...prev, ...updated } : prev))}
            />
          </div>
        )}
      </section>

      <section className="card">
        <div className="collapsible-header" onClick={() => toggleSection("definition")}>
          <h2>Définition SurveyJS</h2>
          <span className={`caret ${expandedSections.definition ? "expanded" : ""}`}>▼</span>
        </div>
        {expandedSections.definition && (
          <div className="collapsible-content">
            <UpdateDefinitionForm
              projectId={projectId!}
              surveyId={surveyId!}
              currentDefinition={survey.definition}
              onUpdated={(updated) => setSurvey((prev) => (prev ? { ...prev, ...updated } : prev))}
            />
          </div>
        )}
      </section>

      <section className="card">
        <div className="collapsible-header" onClick={() => toggleSection("invitations")}>
          <h2>Invitations par courriel</h2>
          <span className={`caret ${expandedSections.invitations ? "expanded" : ""}`}>▼</span>
        </div>
        {expandedSections.invitations && (
          <div className="collapsible-content">
            <InvitationForm
              projectId={projectId!}
              surveyId={surveyId!}
              surveyUrl={SURVEY_URL ?? ""}
              currentEmailSettings={{
                emailSenderName: survey.emailSenderName,
                emailSubject: survey.emailSubject,
                emailBody: survey.emailBody,
                emailSignature: survey.emailSignature,
              }}
              onUpdated={(updated) => setSurvey((prev) => (prev ? { ...prev, ...updated } : prev))}
            />
          </div>
        )}
      </section>

      <section className="card">
        <div className="collapsible-header" onClick={() => toggleSection("welcomePage")}>
          <h2>Page d'accueil (Landing Page)</h2>
          <span className={`caret ${expandedSections.welcomePage ? "expanded" : ""}`}>▼</span>
        </div>
        {expandedSections.welcomePage && (
          <div className="collapsible-content">
            <UpdateWelcomePageForm
              projectId={projectId!}
              surveyId={surveyId!}
              currentWelcomePage={survey.welcomePage}
              onUpdated={(updated) => setSurvey((prev) => (prev ? { ...prev, ...updated } : prev))}
            />
          </div>
        )}
      </section>

      <section className="card">
        <div className="collapsible-header" onClick={() => toggleSection("labels")}>
          <h2>Textes et labels</h2>
          <span className={`caret ${expandedSections.labels ? "expanded" : ""}`}>▼</span>
        </div>
        {expandedSections.labels && (
          <div className="collapsible-content">
            <UpdateLabelsForm
              projectId={projectId!}
              surveyId={surveyId!}
              currentLabels={survey.labels ?? {}}
              onUpdated={(updated) => setSurvey((prev) => (prev ? { ...prev, ...updated } : prev))}
            />
          </div>
        )}
      </section>

      <section className="card">
        <div className="collapsible-header" onClick={() => toggleSection("customJs")}>
          <h2>JavaScript personnalisé (initialisation)</h2>
          <span className={`caret ${expandedSections.customJs ? "expanded" : ""}`}>▼</span>
        </div>
        {expandedSections.customJs && (
          <div className="collapsible-content">
            <UpdateCustomJsForm
              projectId={projectId!}
              surveyId={surveyId!}
              currentCustomJs={survey.customJs ?? ""}
              onUpdated={(updated) => setSurvey((prev) => (prev ? { ...prev, ...updated } : prev))}
            />
          </div>
        )}
      </section>
    </main>
  );
}

function InvitationForm({
  projectId,
  surveyId,
  surveyUrl,
  currentEmailSettings,
  onUpdated,
}: {
  projectId: string;
  surveyId: string;
  surveyUrl: string;
  currentEmailSettings: {
    emailSenderName?: string;
    emailSubject?: string;
    emailBody?: string;
    emailSignature?: string;
  };
  onUpdated: (s: Partial<SurveyWithDefinition>) => void;
}) {
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [invitations, setInvitations] = useState<
    { email: string; name: string; metadata?: Record<string, string> }[]
  >([]);

  const [senderName, setSenderName] = useState(
    currentEmailSettings.emailSenderName ?? "Médaillon Conseils"
  );
  const [subject, setSubject] = useState(
    currentEmailSettings.emailSubject ?? "Votre invitation au sondage"
  );
  const [emailBody, setEmailBody] = useState(
    currentEmailSettings.emailBody ??
      "Vous êtes invité à participer à notre sondage.\n\nVeuillez utiliser le lien unique ci-dessous pour y accéder :\n\n{{link}}\n\nMerci pour votre participation."
  );

  const needsSurveyUrl = emailBody.includes("{{link}}");

  const baseUrl = window.location.origin;
  const defaultSignature = `---
Nathalie Ebnoether, M.A.
Présidente de Médaillon Groupe Conseils

<div style="margin-top: 15px; margin-bottom: 15px;">
  <img src="${baseUrl}/logo-medaillon.png" width="138" height="50" style="display: block; width: 138px; height: 50px;" alt="Logo Médaillon" />
</div>

[www.medaillonconseils.com](https://www.medaillonconseils.com)

<p style="color: #bf9000; font-weight: bold; font-style: italic; margin-top: 15px; margin-bottom: 15px;">
  Une expertise sociologique qui vise à mettre de l’avant la passion de l’humain !
</p>

Pour nous suivre sur Facebook :
[https://www.facebook.com/MedaillonConseils/](https://www.facebook.com/MedaillonConseils/)

Partenaire de [Opubliq.com](https://opubliq.com)

<div style="margin-top: 15px;">
  <img src="${baseUrl}/logo.png" width="150" height="58" style="display: block; width: 150px; height: 58px;" alt="Logo Opubliq" />
</div>`;

  const [emailSignature, setEmailSignature] = useState(
    currentEmailSettings.emailSignature ?? defaultSignature
  );

  useEffect(() => {
    if (currentEmailSettings.emailSenderName) setSenderName(currentEmailSettings.emailSenderName);
    if (currentEmailSettings.emailSubject) setSubject(currentEmailSettings.emailSubject);
    if (currentEmailSettings.emailBody) setEmailBody(currentEmailSettings.emailBody);
    if (currentEmailSettings.emailSignature) setEmailSignature(currentEmailSettings.emailSignature);
  }, [
    currentEmailSettings.emailSenderName,
    currentEmailSettings.emailSubject,
    currentEmailSettings.emailBody,
    currentEmailSettings.emailSignature,
  ]);

  const [submitting, setSubmitting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  async function handleSaveSettings() {
    setError(null);
    setSaveSuccess(false);
    setSaving(true);
    try {
      const updated = await updateSurveyEmailSettings(projectId, surveyId, {
        emailSenderName: senderName,
        emailSubject: subject,
        emailBody,
        emailSignature,
      });
      onUpdated(updated);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erreur lors de l'enregistrement.");
    } finally {
      setSaving(false);
    }
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setCsvFile(file);
    setError(null);
    setSuccess(null);

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const data = results.data as any[];
        const valid = data
          .map((row) => {
            const emailKeys = ["email", "Email", "courriel", "Courriel"];
            const nameKeys = [
              "name",
              "Name",
              "nom",
              "Nom",
              "organisme",
              "Organisme",
              "organization",
              "Organization",
            ];

            const emailKey = Object.keys(row).find((k) => emailKeys.includes(k.trim()));
            const nameKey = Object.keys(row).find((k) => nameKeys.includes(k.trim()));

            const email = (emailKey ? row[emailKey] : "").trim();
            const name = (nameKey ? row[nameKey] : "").trim();

            // Extract metadata (all other columns)
            const metadata: Record<string, string> = {};
            Object.keys(row).forEach((key) => {
              if (key !== emailKey && key !== nameKey) {
                metadata[key] = row[key];
              }
            });

            return { email, name, metadata };
          })
          .filter((row) => row.email && row.email.includes("@"));

        if (valid.length === 0) {
          setError("Aucun email valide trouvé dans le CSV. Colonnes attendues: email, nom.");
        } else {
          setInvitations(valid);
        }
      },
      error: (err) => {
        setError(`Erreur de lecture du CSV: ${err.message}`);
      },
    });
  }

  async function handleSend() {
    if (invitations.length === 0) return;
    setError(null);
    setSuccess(null);
    setSubmitting(true);

    try {
      const { results } = await sendEmails(projectId, surveyId, {
        invitations,
        surveyUrl,
        senderName,
        subject,
        emailBody,
        signature: emailSignature,
      });

      const errors = results.filter((r) => r.status === "error");
      if (errors.length > 0) {
        setError(`${errors.length} erreurs lors de l'envoi. Voir la console.`);
        console.error(
          "Errors sending emails:",
          errors.map((e) => `${e.email}: ${e.error}`)
        );
      } else {
        setSuccess(`${results.length} invitations envoyées avec succès.`);
        setInvitations([]);
        setCsvFile(null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erreur lors de l'envoi.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="create-form">
      <div className="form-row">
        <label>Nom de l'expéditeur</label>
        <input
          type="text"
          value={senderName}
          onChange={(e) => setSenderName(e.target.value)}
          placeholder="Ex: Médaillon Conseils"
        />
      </div>

      <div className="form-row">
        <label>Objet du courriel</label>
        <input
          type="text"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="Objet"
        />
      </div>

      <div className="form-row">
        <label>Corps du courriel (Markdown)</label>
        <p className="field-desc">
          Utilisez <code>{"{{link}}"}</code> pour insérer le lien du sondage.
          Sans ce champ, aucun lien n'est ajouté : le courriel est envoyé tel quel
          (simple diffusion à une liste d'adresses).
        </p>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
            <textarea
              rows={8}
              value={emailBody}
              onChange={(e) => setEmailBody(e.target.value)}
              placeholder="Texte du courriel..."
            />
            <label>Signature (Markdown / HTML)</label>
            <textarea
              rows={8}
              value={emailSignature}
              onChange={(e) => setEmailSignature(e.target.value)}
              placeholder="Signature..."
            />
          </div>
          <div
            className="markdown-preview"
            style={{
              padding: "1rem",
              border: "1px solid #ccc",
              borderRadius: "4px",
              backgroundColor: "#fff",
              overflowY: "auto",
              maxHeight: "500px",
              color: "#333",
            }}
          >
            <ReactMarkdown rehypePlugins={[rehypeRaw]}>
              {emailBody.replace(/{{link}}/g, "[Lien du sondage](https://...)") +
                "\n\n" +
                emailSignature}
            </ReactMarkdown>
          </div>
        </div>
      </div>

      <div className="form-row">
        <label>Importer un fichier CSV (email, nom)</label>
        <input type="file" accept=".csv" onChange={handleFileChange} />
        {invitations.length > 0 && (
          <p className="field-desc">{invitations.length} destinataires prêts.</p>
        )}
      </div>

      {error && <p className="form-error">{error}</p>}
      {success && <p className="form-success">{success}</p>}
      {saveSuccess && <p className="form-success">Paramètres de courriel enregistrés.</p>}

      <div style={{ display: "flex", gap: "1rem", marginTop: "1rem" }}>
        <button
          type="button"
          className="btn-primary"
          disabled={
            submitting ||
            saving ||
            invitations.length === 0 ||
            (needsSurveyUrl && !surveyUrl)
          }
          onClick={handleSend}
        >
          {submitting ? "Envoi en cours…" : "Envoyer les invitations"}
        </button>

        <button
          type="button"
          disabled={submitting || saving}
          onClick={handleSaveSettings}
        >
          {saving ? "Enregistrement…" : "Enregistrer les paramètres de courriel"}
        </button>
      </div>
      {needsSurveyUrl && !surveyUrl && (
        <p className="form-error">
          Impossible d'envoyer : l'URL du sondage n'est pas configurée.
        </p>
      )}
    </div>
  );
}

function UpdateMetadataForm({
  projectId,
  surveyId,
  currentName,
  onUpdated,
}: {
  projectId: string;
  surveyId: string;
  currentName: string;
  onUpdated: (s: Partial<SurveyWithDefinition>) => void;
}) {
  const [name, setName] = useState(currentName);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    setSubmitting(true);

    try {
      const updated = await updateSurveyName(projectId, surveyId, name);
      onUpdated(updated);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erreur lors de la mise à jour.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="create-form">
      <div className="form-row">
        <label htmlFor="survey-name">Nom du sondage</label>
        <p className="field-desc">Le titre interne du sondage.</p>
        <input
          id="survey-name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </div>
      {error && <p className="form-error">{error}</p>}
      {success && <p className="form-success">Nom mis à jour.</p>}
      <button type="submit" className="btn-primary" disabled={submitting}>
        {submitting ? "Enregistrement…" : "Enregistrer le nom"}
      </button>
    </form>
  );
}

function UpdateSlugForm({
  projectId,
  surveyId,
  currentSlug,
  onUpdated,
}: {
  projectId: string;
  surveyId: string;
  currentSlug: string;
  onUpdated: (s: Partial<SurveyWithDefinition>) => void;
}) {
  const [slug, setSlug] = useState(currentSlug);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    setSubmitting(true);

    try {
      const updated = await updateSurveySlug(projectId, surveyId, slug);
      onUpdated(updated);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erreur lors de la mise à jour.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="create-form">
      <div className="form-row">
        <label htmlFor="slug">Slug (URL personnalisée)</label>
        <p className="field-desc">Le nom qui apparaîtra dans l'URL (ex: sondage-usagers).</p>
        <input id="slug" type="text" value={slug} onChange={(e) => setSlug(e.target.value)} />
      </div>
      {error && <p className="form-error">{error}</p>}
      {success && <p className="form-success">Slug mis à jour.</p>}
      <button type="submit" className="btn-primary" disabled={submitting}>
        {submitting ? "Enregistrement…" : "Enregistrer le slug"}
      </button>
    </form>
  );
}

function UpdateDefinitionForm({
  projectId,
  surveyId,
  currentDefinition,
  onUpdated,
}: {
  projectId: string;
  surveyId: string;
  currentDefinition: unknown;
  onUpdated: (s: Partial<SurveyWithDefinition>) => void;
}) {
  const [defJson, setDefJson] = useState(JSON.stringify(currentDefinition, null, 2));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  function handleFileUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target?.result as string;
      try {
        const parsed = JSON.parse(text);
        setDefJson(JSON.stringify(parsed, null, 2));
        setError(null);
      } catch {
        setError("Fichier JSON invalide.");
      }
    };
    reader.readAsText(file);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    let definition: unknown;
    try {
      definition = JSON.parse(defJson);
    } catch {
      setError("JSON invalide.");
      return;
    }

    setSubmitting(true);
    try {
      const updated = await updateSurveyDefinition(projectId, surveyId, definition);
      onUpdated({ ...updated, definition });
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch {
      setError("Erreur lors de la mise à jour.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="create-form">
      <div className="form-row">
        <label>Importer un fichier JSON</label>
        <input
          ref={fileInputRef}
          type="file"
          accept=".json,application/json"
          onChange={handleFileUpload}
        />
      </div>
      <div className="form-row">
        <label htmlFor="defJson">Définition JSON</label>
        <textarea
          id="defJson"
          rows={12}
          value={defJson}
          onChange={(e) => setDefJson(e.target.value)}
        />
      </div>
      {error && <p className="form-error">{error}</p>}
      {success && <p className="form-success">Définition mise à jour.</p>}
      <button type="submit" className="btn-primary" disabled={submitting}>
        {submitting ? "Enregistrement…" : "Enregistrer la définition"}
      </button>
    </form>
  );
}

function UpdateCustomJsForm({
  projectId,
  surveyId,
  currentCustomJs,
  onUpdated,
}: {
  projectId: string;
  surveyId: string;
  currentCustomJs: string;
  onUpdated: (s: Partial<SurveyWithDefinition>) => void;
}) {
  const [jsCode, setJsCode] = useState(currentCustomJs);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    setSubmitting(true);

    try {
      const updated = await updateSurveyCustomJs(projectId, surveyId, jsCode);
      onUpdated(updated);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch {
      setError("Erreur lors de la mise à jour.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="create-form">
      <div className="form-row">
        <label htmlFor="jsCode">Code JavaScript</label>
        <p className="field-desc">
          Ce code sera exécuté lors de l'initialisation du sondage. L'objet <code>survey</code> est
          disponible.
        </p>
        <textarea
          id="jsCode"
          rows={8}
          value={jsCode}
          onChange={(e) => setJsCode(e.target.value)}
          placeholder='const versions = ["A", "B", "C"];&#10;const randomVersion = versions[Math.floor(Math.random() * 3)];&#10;survey.setVariable("q6_version", randomVersion);'
        />
      </div>
      {error && <p className="form-error">{error}</p>}
      {success && <p className="form-success">JavaScript mis à jour.</p>}
      <button type="submit" className="btn-primary" disabled={submitting}>
        {submitting ? "Enregistrement…" : "Enregistrer le JavaScript"}
      </button>
    </form>
  );
}

function UpdateLabelsForm({
  projectId,
  surveyId,
  currentLabels,
  onUpdated,
}: {
  projectId: string;
  surveyId: string;
  currentLabels: NonNullable<SurveyWithDefinition["labels"]>;
  onUpdated: (s: Partial<SurveyWithDefinition>) => void;
}) {
  const [completeText, setCompleteText] = useState(currentLabels.completeText ?? "");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    setSubmitting(true);

    try {
      const labels = {
        completeText: completeText.trim() || undefined,
      };
      const updated = await updateSurveyLabels(projectId, surveyId, labels);
      onUpdated(updated);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch {
      setError("Erreur lors de la mise à jour.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="create-form">
      <div className="form-row">
        <label htmlFor="completeText">Bouton de fin (ex: Terminer, Envoyer)</label>
        <input
          id="completeText"
          type="text"
          value={completeText}
          onChange={(e) => setCompleteText(e.target.value)}
          placeholder="Par défaut: Terminer"
        />
      </div>
      {error && <p className="form-error">{error}</p>}
      {success && <p className="form-success">Labels mis à jour.</p>}
      <button type="submit" className="btn-primary" disabled={submitting}>
        {submitting ? "Enregistrement…" : "Enregistrer les labels"}
      </button>
    </form>
  );
}

function UpdateWelcomePageForm({
  projectId,
  surveyId,
  currentWelcomePage,
  onUpdated,
}: {
  projectId: string;
  surveyId: string;
  currentWelcomePage?: SurveyWithDefinition["welcomePage"];
  onUpdated: (s: Partial<SurveyWithDefinition>) => void;
}) {
  const [config, setConfig] = useState(
    currentWelcomePage || {
      title: "",
      welcomeText: "",
      expandableTitle: "",
      expandableContent: "",
      isExpandable: true,
      moreInfoUrl: "",
      moreInfoLabel: "",
    }
  );
  // Ensure isExpandable has a default if config exists but isExpandable is missing
  if (config.isExpandable === undefined) {
    config.isExpandable = true;
  }
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    setSubmitting(true);

    try {
      const updated = await updateSurveyWelcomePage(projectId, surveyId, config);
      onUpdated(updated);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch {
      setError("Erreur lors de la mise à jour.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="create-form">
      <div className="form-row">
        <label htmlFor="wp-title">Titre</label>
        <input
          id="wp-title"
          type="text"
          value={config.title}
          onChange={(e) => setConfig({ ...config, title: e.target.value })}
          placeholder="Bienvenue"
        />
      </div>
      <div className="form-row">
        <label htmlFor="wp-text">Texte de bienvenue</label>
        <textarea
          id="wp-text"
          rows={4}
          value={config.welcomeText}
          onChange={(e) => setConfig({ ...config, welcomeText: e.target.value })}
        />
      </div>
      <div className="form-row">
        <label htmlFor="wp-exp-title">Titre section dépliable</label>
        <input
          id="wp-exp-title"
          type="text"
          value={config.expandableTitle}
          onChange={(e) => setConfig({ ...config, expandableTitle: e.target.value })}
          placeholder="Qui sommes-nous ?"
        />
      </div>
      <div className="form-row checkbox-row" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
        <input
          id="wp-is-exp"
          type="checkbox"
          checked={config.isExpandable}
          onChange={(e) => setConfig({ ...config, isExpandable: e.target.checked })}
        />
        <label htmlFor="wp-is-exp" style={{ marginBottom: 0 }}>Afficher la section d'information dépliable</label>
      </div>
      <div className="form-row">
        <label htmlFor="wp-exp-content">Contenu section dépliable</label>
        <textarea
          id="wp-exp-content"
          rows={4}
          value={config.expandableContent}
          onChange={(e) => setConfig({ ...config, expandableContent: e.target.value })}
        />
      </div>
      <div className="form-row">
        <label htmlFor="wp-url">URL "En savoir plus"</label>
        <input
          id="wp-url"
          type="url"
          value={config.moreInfoUrl}
          onChange={(e) => setConfig({ ...config, moreInfoUrl: e.target.value })}
          placeholder="https://..."
        />
      </div>
      <div className="form-row">
        <label htmlFor="wp-label">Libellé "En savoir plus"</label>
        <input
          id="wp-label"
          type="text"
          value={config.moreInfoLabel}
          onChange={(e) => setConfig({ ...config, moreInfoLabel: e.target.value })}
          placeholder="Par défaut: Pour en savoir plus"
        />
      </div>
      {error && <p className="form-error">{error}</p>}
      {success && <p className="form-success">Configuration mise à jour.</p>}
      <button type="submit" className="btn-primary" disabled={submitting}>
        {submitting ? "Enregistrement…" : "Enregistrer la configuration"}
      </button>
    </form>
  );
}
